import { format, isBefore, FormatOptions } from 'date-fns';

export const parseToDate = (dateString: string): Date => {
    // Check if date already has timezone information
    const hasTimezoneInfo =
        dateString.includes('Z') || dateString.includes('+') || dateString.includes('T');

    if (hasTimezoneInfo) {
        return new Date(dateString);
    }

    // Convert to ISO format for Safari compatibility
    const isoString = dateString.includes(' ')
        ? `${dateString.replace(' ', 'T')}Z` // "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ssZ"
        : `${dateString}T00:00:00Z`; // "YYYY-MM-DD" -> "YYYY-MM-DDTHH:mm:ssZ"

    return new Date(isoString);
};

export const getFormattedDate = (date: Date, dateFormat: string, options?: FormatOptions) => {
    return format(date, dateFormat, options);
};

export const getIsBeforeComparedDate = (date: Date, comparedDate: Date) => {
    return isBefore(date, comparedDate);
};

export const getBillingFormattedDate = (
    utcDate: string,
    dateFormat: string,
    options?: FormatOptions
) => {
    // utcDate example: 2025-08-14 02:10:10
    // Parse as UTC and convert to EST/EDT
    const utcDateTime = new Date(utcDate + 'Z');

    // Get the date in EST/EDT timezone
    const estDateString = utcDateTime.toLocaleString('en-US', {
        timeZone: 'America/New_York'
    });
    const estDate = new Date(estDateString);

    const dateString = format(estDate, dateFormat, options);

    return `${dateString} (ET)`;
};

export function createUTCDateRangeForBackend(fromDate: string, toDate: string) {
    // Create dates in user's LOCAL timezone first
    const startLocal = new Date(fromDate + 'T00:00:00'); // Start of day in user's timezone
    const endLocal = new Date(toDate + 'T23:59:59.999'); // End of day in user's timezone

    // toISOString() automatically converts local time to UTC
    // This gives us the UTC time range that corresponds to the local date range
    const formatDateTime = (date: Date): string => {
        return date.toISOString().slice(0, 19).replace('T', ' ');
    };

    return {
        from: formatDateTime(startLocal),
        to: formatDateTime(endLocal),
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // For debugging
        localStart: startLocal.toString(),
        localEnd: endLocal.toString(),
        utcStart: startLocal.toISOString(),
        utcEnd: endLocal.toISOString()
    };
}

export const getUtcDateFormat = (date?: string, dateFormat?: string) => {
    const dateT = date ? new Date(date) : undefined;
    if (dateT) {
        const dateObj = new Date(dateT.getUTCFullYear(), dateT.getUTCMonth(), dateT.getUTCDate());

        return format(dateObj, dateFormat || 'MMM d, yyyy');
    }

    return 'N/A';
};

export const getDOBFormat = (date?: string | null, dateFormat?: string) => {
    const dob = date ? new Date(date) : undefined;

    if (dob && !Number.isNaN(dob.getTime())) {
        const hours = dob.getUTCHours();

        const dateObj = new Date(
            Date.UTC(
                dob.getUTCFullYear(),
                dob.getUTCMonth(),
                dob.getUTCDate(),
                hours === 0 ? 12 : dob.getUTCHours(),
                dob.getUTCMinutes(),
                dob.getUTCSeconds()
            )
        );

        return format(dateObj, dateFormat || 'MM-dd-yyyy');
    }

    return '';
};

// `John Smith` -> `John Smith (08-26-1958)`; the name alone when the DOB is missing or unusable.
export const withDob = (name: string, dateOfBirth?: string | null) => {
    const dob = getDOBFormat(dateOfBirth);

    return dob ? `${name} (${dob})` : name;
};
